import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { GoogleMap, useLoadScript, MarkerF } from '@react-google-maps/api';
import { Box, VStack, HStack, Image, Text, useColorModeValue, useColorMode } from '@chakra-ui/react';
import { useSublocations } from '../../hooks/useSublocations';
import { useMapData } from '../../hooks/useMapData';
import SubLocationInfoWindow from '../SubLocationInfoWindow';
import DarkModeInfoWindow from '../DarkModeInfoWindow';
import { GOOGLE_MAPS_API_KEY } from '../../config/googleMapsConfig';
import { AUTO_DETECT_MAPPING } from '../../constants/autoDetectMapping';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const mapStyles = {
  light: [],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ color: '#263c3f' }],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#6b9a76' }],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }],
    },
  ],
};

export default function SiteLocationMap({ siteId, mainLocation, onMediaClick, media, filteredMedia, filters, hiddenLocations = {} }) {
  const { colorMode } = useColorMode();
  const boxBorderColor = useColorModeValue('gray.200', 'gray.700');
  
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const { sublocations } = useSublocations(siteId);
  const { activeMarkerId, setActiveMarkerId } = useMapData();

  console.log(`[SiteLocationMap] Loaded ${sublocations?.length || 0} sublocations for siteId: ${siteId}`);
  if (sublocations?.length > 0) {
    console.log('[SiteLocationMap] First sublocation coordinates:', 
      sublocations[0]?.coordinates ? 
      `lat: ${sublocations[0].coordinates.lat}, lng: ${sublocations[0].coordinates.lng}` : 
      'No coordinates');
  }

  // Filter sublocations based on visibility state
  const visibleSublocations = useMemo(() => {
    if (!sublocations) return [];
    const filtered = sublocations.filter(sublocation => !hiddenLocations[sublocation.id]);
    console.log(`[SiteLocationMap] Filtered to ${filtered.length} visible sublocations`);
    return filtered;
  }, [sublocations, hiddenLocations]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: true,
    mapTypeControlOptions: {
      style: window.google?.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: window.google?.maps.ControlPosition.LEFT_TOP,
    },
    zoomControlOptions: {
      position: window.google?.maps.ControlPosition.RIGHT_CENTER,
    },
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: window.google?.maps.ControlPosition.TOP_LEFT,
    },
    streetViewControl: false,
    mapTypeId: 'roadmap',
    backgroundColor: colorMode === 'dark' ? '#242f3e' : '#fff',
    styles: colorMode === 'dark' ? mapStyles.dark : mapStyles.light,
  }), [colorMode]);

  // Marker sizes for different zoom levels
  const markerSizes = useMemo(() => {
    if (!isLoaded) return { main: null, sub: null };
    return {
      main: new window.google.maps.Size(40, 40),
      sub: new window.google.maps.Size(30, 37)
    };
  }, [isLoaded]);

  // Default center if no main location
  const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 }; // San Francisco

  const center = useMemo(() => {
    if (!mainLocation) return DEFAULT_CENTER;
    
    // Handle both coordinate formats (lat/lng or latitude/longitude)
    return {
      lat: mainLocation.lat || mainLocation.latitude,
      lng: mainLocation.lng || mainLocation.longitude,
    };
  }, [mainLocation]);

  if (!isLoaded) return null;

  return (
    <Box
      borderRadius="lg" 
      overflow="hidden" 
      border="1px solid"
      borderColor={boxBorderColor}
    >
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={16}
        options={mapOptions}
        onClick={() => setActiveMarkerId(null)}
      >
        {/* Main site marker */}
        {mainLocation && (
          <MarkerF
            position={center}
            icon={{
              url: '/marker-main.svg',
              scaledSize: markerSizes.main,
            }}
            animation={isLoaded ? window.google.maps.Animation.DROP : null}
          />
        )}

        {/* Sublocation markers */}
        {visibleSublocations.map((sublocation) => {
          // Normalize coordinates to handle both lat/lng and latitude/longitude formats
          const normalizedCoords = sublocation.coordinates ? {
            lat: sublocation.coordinates.lat || sublocation.coordinates.latitude,
            lng: sublocation.coordinates.lng || sublocation.coordinates.longitude
          } : null;
          
          // Additional coordinate validation and logging
          const hasValidCoords = normalizedCoords && 
                               typeof normalizedCoords.lat === 'number' && 
                               typeof normalizedCoords.lng === 'number';
          
          console.log(`[SiteLocationMap] Sublocation ${sublocation.id} has valid coordinates: ${hasValidCoords}`, 
                     hasValidCoords ? normalizedCoords : 'Invalid coordinates');
          
          if (!hasValidCoords) return null;
          
          return (
            <React.Fragment key={sublocation.id}>
              <MarkerF
                position={normalizedCoords}
                icon={{
                  url: '/marker-sub.svg',
                  scaledSize: markerSizes.sub,
                }}
                animation={isLoaded ? window.google.maps.Animation.DROP : null}
                onClick={() => setActiveMarkerId(sublocation.id)}
              />
              
              {activeMarkerId === sublocation.id && (
                <DarkModeInfoWindow
                  position={normalizedCoords}
                  onCloseClick={() => setActiveMarkerId(null)}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -30),
                    maxWidth: 400,
                    backgroundColor: colorMode === 'dark' ? '#242f3e' : '#fff'
                  }}
                  colorMode={colorMode}
                >
                  <SubLocationInfoWindow
                    sublocation={{
                      ...sublocation,
                      media: filters?.features?.length > 0
                        ? filteredMedia?.filter(m => m.subLocationId === sublocation.id)
                        : media?.filter(m => m.subLocationId === sublocation.id)
                    }}
                    onMediaClick={(image) => onMediaClick(image.url)}
                  />
                </DarkModeInfoWindow>
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>
    </Box>
  );
}
